import React from "react";
import { Link } from "react-router-dom";
import type { NotificationEmailCategory, NotificationGroup, NotificationItem } from "@globalbridge/types";
import { CardHeader } from "./Card";
import { notificationWhen } from "../../../utils/notificationWhen";
import { MARKETPLACE_LABEL, NOTIF_EMAIL_ROWS, NOTIF_PAGE_LABEL } from "./notificationsCopy";
import "./notifications.css";

/**
 * Notifications primitives (mock GB-redesign-spec/notifications-mock,
 * approved 2026-10-08): the item row, a group card, the email card, the
 * switch and the bell dropdown. Pure: data and callbacks in, markup out.
 */

/** Mock `.sw`: a 34x20 switch over a real checkbox. */
export const NotifSwitch: React.FC<{
  checked: boolean;
  onChange: (on: boolean) => void;
  label: string;
  disabled?: boolean;
}> = ({ checked, onChange, label, disabled }) => (
  <label className="ds-n-sw">
    <input type="checkbox" aria-label={label} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    <span />
  </label>
);

export interface NotifItemProps {
  item: NotificationItem;
  /** Bell dropdown: no source line, no digest list. */
  compact?: boolean;
  /** Called with the item before the app follows its link. */
  onOpen: (item: NotificationItem) => void;
  now?: Date;
}

/** One notification (mock `.item`). The whole row opens `item.link`. */
export const NotifItem: React.FC<NotifItemProps> = ({ item, compact, onOpen, now }) => {
  const open = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen(item);
  };
  const digest = !compact && item.items.length > 0;
  const body = (
    <>
      <span className="ds-n-ud" aria-label={item.unread ? "Unread" : undefined} />
      <span className="ds-n-t">{item.title}</span>
      <span className="ds-n-when">{notificationWhen(item.sourceAt, now)}</span>
      <span className="ds-n-b">{item.body}</span>
      <span className="ds-n-meta">
        {item.pages.map((p) => (
          <span key={p} className="ds-n-page">
            {NOTIF_PAGE_LABEL[p]}
          </span>
        ))}
        {item.marketplaces.map((m) => (
          <span key={m} className="ds-n-mp" data-mp={m}>
            <i aria-hidden="true" />
            {MARKETPLACE_LABEL[m]}
          </span>
        ))}
        {digest ? (
          <Link className="ds-n-go" to={item.link} onClick={open}>
            {item.linkLabel} →
          </Link>
        ) : (
          <span className="ds-n-go">{item.linkLabel} →</span>
        )}
        {!compact && (
          <span className="ds-n-src" title={`From: ${item.source}. Opens ${item.link}`}>
            From {item.source}
          </span>
        )}
      </span>
    </>
  );
  if (digest) {
    return (
      // Holds links of its own, so the row is not itself a link: a click on
      // the row opens the digest's page; keyboard users take the links.
      <div className="ds-n-item" data-unread={item.unread ? "" : undefined} onClick={open}>
        {body}
        <ul className="ds-n-digest">
          {item.items.map((d) => (
            <li key={`${d.link}|${d.title}`}>
              <Link
                to={d.link}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen({ ...item, link: d.link });
                  e.preventDefault();
                }}
              >
                <span className="ds-n-page">{NOTIF_PAGE_LABEL[d.page]}</span>
                {d.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <a className="ds-n-item" href={item.link} data-unread={item.unread ? "" : undefined} onClick={open}>
      {body}
    </a>
  );
};

/** A group card (mock `.card.grp`): header, count, its items or its empty line. */
export const NotifGroupCard: React.FC<{
  group: NotificationGroup;
  title: string;
  hint: string;
  count: number;
  empty: string;
  children?: React.ReactNode;
  onMore?: () => void;
}> = ({ group, title, hint, count, empty, children, onMore }) => (
  <section className="ds-card ds-n-grp" data-g={group} aria-label={title}>
    <div className="ds-n-grp-h">
      <h2>{title}</h2>
      {count > 0 && <span className="ds-n-cnt num">{count}</span>}
      <span className="ds-hint">{hint}</span>
    </div>
    {count > 0 ? children : <div className="ds-n-empty">{empty}</div>}
    {onMore && (
      <button type="button" className="ds-n-more" onClick={onMore}>
        Show older
      </button>
    )}
  </section>
);

/** "Email me about" (mock `.es`): one switch per kind, the address line. */
export const NotifEmailCard: React.FC<{
  values: Partial<Record<NotificationEmailCategory, boolean>>;
  email: string | null;
  onToggle: (key: NotificationEmailCategory, on: boolean) => void;
  disabled?: boolean;
}> = ({ values, email, onToggle, disabled }) => (
  <aside className="ds-card ds-n-es" id="email" aria-label="Email me about">
      <CardHeader title="Email me about" right="Just you" />
      <p className="ds-n-es-intro">
        Everything shows here and on the bell. Turn on email for the kinds you want in your inbox too.
      </p>
      {NOTIF_EMAIL_ROWS.map(([key, label, sub]) => (
        <div key={key} className="ds-n-es-row">
          <b>{label}</b>
          <NotifSwitch
            label={`Email for ${label}`}
            checked={values[key] ?? false}
            disabled={disabled || values[key] === undefined}
            onChange={(on) => onToggle(key, on)}
          />
          <p>{sub}</p>
        </div>
      ))}
      <div className="ds-n-es-to">
        {email ? (
          <>
            Emails go to <b>{email}</b>.{" "}
          </>
        ) : null}
        Each teammate picks their own. A shipment notice copy always goes to the person who filed it.
      </div>
  </aside>
);

/** The bell's panel (mock `.dd`): Needs you first (3, then "N more"), the latest 3, the footer. */
export const NotifDropdown: React.FC<{
  needs: NotificationItem[];
  latest: NotificationItem[];
  needsYou: number;
  unread: number;
  onOpen: (item: NotificationItem) => void;
  /** Absent while GB staff view a brand: reading there marks nothing read. */
  onMarkAll?: () => void;
  onSeeAll: () => void;
  onEmail: () => void;
  now?: Date;
}> = ({ needs, latest, needsYou, unread, onOpen, onMarkAll, onSeeAll, onEmail, now }) => (
  <div className="ds-n-dd" role="dialog" aria-label="Latest notifications">
    <div className="ds-n-dd-h">
      <h3>Notifications</h3>
      {onMarkAll && (
        <button type="button" onClick={onMarkAll}>
          Mark all read
        </button>
      )}
    </div>
    <div className="ds-n-dd-sub">
      <b className="num">{needsYou}</b> need you · <span className="num">{unread}</span> unread
    </div>
    <div className="ds-n-dd-list">
      <div className="ds-n-dd-lbl">Needs you</div>
      {needs.length === 0 && <div className="ds-n-dd-empty">Nothing needs you.</div>}
      {needs.slice(0, 3).map((x) => (
        <NotifItem key={x.id} item={x} compact onOpen={onOpen} now={now} />
      ))}
      {needsYou > 3 && (
        <button type="button" className="ds-n-dd-more" onClick={onSeeAll}>
          {needsYou - 3} more need you →
        </button>
      )}
      <div className="ds-n-dd-lbl">Latest</div>
      {latest.length === 0 && <div className="ds-n-dd-empty">Nothing new.</div>}
      {latest.slice(0, 3).map((x) => (
        <NotifItem key={x.id} item={x} compact onOpen={onOpen} now={now} />
      ))}
    </div>
    <div className="ds-n-dd-f">
      <a
        href="/notifications"
        onClick={(e) => {
          e.preventDefault();
          onSeeAll();
        }}
      >
        See all notifications
      </a>
      <a
        href="/notifications#email"
        onClick={(e) => {
          e.preventDefault();
          onEmail();
        }}
      >
        Email settings
      </a>
    </div>
  </div>
);
